import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import { saveOAuthRedirect, clearOAuthRedirect } from "@/lib/oauthRedirect";
import { toast } from "sonner";

// Pantalla de consentimiento del conector @Olimpo. ChatGPT abre esta URL (con sus parámetros OAuth).
// Si no hay sesión, mandamos al login guardando el destino; con sesión, un solo botón: PERMITIR.
export default function OAuthAuthorize() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enviando, setEnviando] = useState(false);

  const params = useMemo(() => Object.fromEntries(new URLSearchParams(window.location.search).entries()), []);

  useEffect(() => {
    if (user === undefined) return;          // auth todavía cargando: no guardar (evita bucles)
    if (user === null) {
      saveOAuthRedirect(window.location);     // preserve the validated OAuth return
      navigate("/login", { replace: true });  // avoid an extra sign-in interstitial
    } else {
      clearOAuthRedirect();                   // ya llegamos autenticados a /oauth/authorize: limpiar
    }
  }, [user, navigate]);

  const decidir = async (aprobar) => {
    setEnviando(true);
    try {
      const r = await api.post("/oauth/authorize", { ...params, approve: aprobar ? "yes" : "no" });
      if (r.data && r.data.redirect) {
        window.location.assign(r.data.redirect);
      } else {
        toast.error("No pude completar la conexión.");
        setEnviando(false);
      }
    } catch (e) {
      toast.error(errText(e));
      setEnviando(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-negro flex items-center justify-center">
        <div className="w-14 h-14 rounded-full grad-dorado glow-dorado animate-breathe" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-negro flex items-center justify-center px-5" data-testid="oauth-authorize">
      <div className="w-full" style={{ maxWidth: 440, background: "#0F0F0F", border: "1px solid #1F1F1F", borderRadius: 18, padding: 28 }}>
        <div className="flex items-center gap-2 mb-5">
          <div className="rounded-full grad-dorado" style={{ width: 10, height: 10 }} />
          <span className="font-sans" style={{ color: "#C9A84C", fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>Conector @Olimpo</span>
        </div>

        <h1 className="font-sans" style={{ fontSize: 22, color: "#F0F0F0", lineHeight: 1.25, marginBottom: 14 }}>
          ChatGPT quiere conectarse a tu Olimpo
        </h1>
        <p className="font-sans" style={{ fontSize: 14, color: "#B8B8B8", lineHeight: 1.6, marginBottom: 22 }}>
          Va a poder leer el cerebro de tu empresa y tus procesos, y avisarnos cómo salió cada corrida.
        </p>

        {user === null ? (
          <>
            <p className="font-sans" style={{ fontSize: 13, color: "#EC835A", marginBottom: 16 }}>
              Entrá con tu cuenta de Olimpo. Si es tu primera vez, completá el alta y la bienvenida; después volverás automáticamente acá para permitir la conexión.
            </p>
            <a href="/login" data-testid="oauth-ir-login" className="font-sans block text-center rounded-xl"
              style={{ background: "#C9A84C", color: "#0E0E0E", fontSize: 15, fontWeight: 600, padding: "12px 0" }}>
              Iniciar sesión
            </a>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="font-sans" style={{ fontSize: 12, color: "#7A7A7A", marginBottom: 4 }}>
              Conectando como <span style={{ color: "#D8D8D8" }}>{user.email}</span>
            </div>
            <button onClick={() => decidir(true)} disabled={enviando} data-testid="oauth-permitir" className="font-sans rounded-xl"
              style={{ background: "#C9A84C", color: "#0E0E0E", fontSize: 15, fontWeight: 600, padding: "12px 0", opacity: enviando ? 0.6 : 1 }}>
              {enviando ? "Conectando…" : "PERMITIR"}
            </button>
            <button onClick={() => decidir(false)} disabled={enviando} data-testid="oauth-cancelar" className="font-sans rounded-xl"
              style={{ background: "#161616", border: "1px solid #2A2A2A", color: "#B8B8B8", fontSize: 14, padding: "10px 0" }}>
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
